import React, { Component } from 'react';
import CobrowseAPI from 'cobrowse-agent-sdk';
import Stopwatch from 'COMPONENTS/CommonComponents/Stopwatch/Stopwatch';
import { getMessage } from "CONFIG/i18n";
import toastr from 'COMPONENTS/ShowTostr/ShowTostr';
import { COMPONENT_IN_FOCUS } from "../../Utils/Constants";

// Style
import Spinner from "@/Components/SpinnerForContainer/SpinnerForContainer";
import './InboundCoBrowse.less';
import { Label } from "reactstrap";

// Actions
import { sessionActions, inboundCoBrowseAction } from "CONFIG/ActionFactory";

// Icons
import ic_laser from "ASSETS/Images/inbound_cobrowse/ic_laser.svg";
import ic_pencil from "ASSETS/Images/inbound_cobrowse/ic_pencil.svg";
import ic_trash from "ASSETS/Images/inbound_cobrowse/ic_trash.svg";
import ic_end_call from "ASSETS/Images/inbound_cobrowse/ic_end_call.svg";
import ic_remote_control from "ASSETS/Images/inbound_cobrowse/ic_remote_control.svg";

const colors = ['#e94435', '#34a853', '#000000', '#FFFF00', '#10b5cb', '#FF00FF']; // Red, Green, Black, Yellow, Blue, Magenta

class InboundCobrowse extends Component {
  constructor(props) {
    super(props);

    this.state = {
      session: null,
      error: null,
      tool: 'laser',
      context: null,
      screenInfo: null,
      iFrameSrc: null,
      showColors: false,
      selectedColor: localStorage.getItem('cbio_selected_color') || colors[0],
      showColorPicker: false, 
      lastDisconnectedToastrTime: 0,
      wasDisconnected: false,
      maskSearchingForDevice: true,
    };

    this.onIframeRef = this.onIframeRef.bind(this);
    this.pickTool = this.pickTool.bind(this);
  }

  componentDidMount() {
    console.info("InboundCobrowse mount");
    this.iFrameSrc();
    this.startToastrCheckInterval();
  }

  componentWillUnmount() {
    console.info("InboundCobrowse unmount");
    clearInterval(this.toastrInterval);
  }

  startToastrCheckInterval() {
    this.toastrInterval = setInterval(() => {
      const { session, screenInfo, lastDisconnectedToastrTime, wasDisconnected } = this.state;
  
      if (session?.state === 'active' && screenInfo?.updated) {
        const updated = new Date(screenInfo.updated);
        const delta = Date.now() - updated.getTime();
  
        if (delta > 10 * 1000) {
          const now = Date.now();
          if (now - lastDisconnectedToastrTime > window._env_.REACT_APP_INBOUND_COBROWSE_LAST_DISCONNECTED_TOASTR_TIME * 1000) {
            this.setState({
              lastDisconnectedToastrTime: now,
              wasDisconnected: true,
            });
            toastr.error(getMessage("INBOUND_CO_BROWSE_DISCONNECTED"));
          }
        } else if (wasDisconnected) {
          // Reconnected scenario
          this.setState({ wasDisconnected: false });
          toastr.success(getMessage("INBOUND_CO_BROWSE_RECONNECTED"));
        }
      }
    }, 3000);
  }

  renderColorPicker() {
    const { showColorPicker } = this.state;
    if (!showColorPicker) return null;
  
    return (
      <div className="color-picker-popup">
        {colors.map((color, index) => (
          <div
            key={index}
            className="color-option"
            style={{ backgroundColor: color }}
            onClick={() => this.handleColorChange(color)}
          />
        ))}
      </div>
    );
  }

  handleSetInboundCoBrowseLogs = async () => {
    const requestData = {
      sessionKey: this.props.sessionKey,
    };
    let data = await inboundCoBrowseAction.saveInboundCobrowseLogs(requestData).then(
      (response) => {
        return response;
      },
      (error) => {
        console.error(error);
        toastr.error(getMessage(error.code));
        return error;
      }
    );
  }

  componentDidUpdate(prevProps, prevState) {
    const { session, screenInfo, tool, error } = this.state;
  
    if (prevState.session?.state !== session?.state) {
      if (session?.state === 'pending') {
        this.setState({maskSearchingForDevice: false});
        toastr.info(getMessage("INBOUND_CO_BROWSE_INITIALIZING"));
      }
  
      if (session?.state === 'authorizing') {
        toastr.info(getMessage("INBOUND_CO_BROWSE_PERMISSION"));
      }

      if (session?.state === 'active') {
        this.handleSetInboundCoBrowseLogs();
        toastr.success(getMessage("INBOUND_CO_BROWSE_SESSION_STARTED"));
      }

      if (session?.state === 'ended') {
        toastr.info(getMessage("INBOUND_CO_BROWSE_ENDED"));
        this.handleEndCall();
      }
    }
        
    if (prevState.error !== error) {
      toastr.error(getMessage("INBOUND_CO_BROWSE_SYSTEM_ERROR", { message: error.message }));
      console.error(`Inbound cobrowse error: ${error.id}: ${error.message}`)
      this.handleEndCall();
    }

    if (prevState.tool !== tool) {
      this.state.context?.setTool(tool);
    }
  
    if (!prevState.screenInfo?.width && screenInfo?.width) {
      console.log('Screen info updated with width:', screenInfo.width);
    }
  
    if (!screenInfo?.width && prevState.screenInfo?.width) {
      console.log('Screen width is now undefined or missing.');
      // Handle when screen info width disappears
    }
    
  }
  
  iFrameSrc = () => {
    const { data } = this.props;
    
    if (data?.status) {
      inboundCoBrowseAction.showHideInboundCoBrowse(false);
      return;
    }

    // Check if any variable is missing
    if (!data?.api || !data?.sessionKey || !data?.token) {
      toastr.error(getMessage("INBOUND_CO_BROWSE_MISSING_REQUIRED_PARAMS"));
      return;  // or return a default value like 'Error: Missing parameters'
    }

    // If all variables are present, construct the iFrameSrc
    const iFrameSrc = `${data.api}/connect?filter_eXcloserSessionKey=${data.sessionKey}&token=${data.token}
      &end_action=none&agent_tools=yes&device_controls=none&session_details=none&popout=yes&messages=none`;

    this.setState({iFrameSrc: iFrameSrc});
  }

  async onIframeRef(iframe) {
    if (!this.state.context && iframe) {
      try {
        // Wait for the iframe to load
        if (!iframe.contentWindow) {
          console.error("Iframe content window is not available.");
          return;
        }
  
        const cobrowse = new CobrowseAPI(null, { api: this.props.data?.api });
        const ctx = await cobrowse.attachContext(iframe);
  
        window.cobrowse_ctx = ctx;
  
        // Attach event listeners only after context is initialized
        ctx.on('session.updated', (session) => {
          console.log("inboundCobrowse session.updated", session.isEnded(), session.toJSON());
          this.setState({ session: session.toJSON() });
          if (session.isEnded()) {
            ctx.destroy();
            this.setState({ context: null });
          }
        });
  
        ctx.on('screen.updated', (info) => {
          this.setState({ screenInfo: info });
        });
  
        ctx.on('error', (err) => {
          console.log("inboundCobrowse error", err);
          this.setState({ error: err });
        });
  
        this.setState({ context: ctx });
      } catch (error) {
        console.error("❌ Error initializing Cobrowse context:", error);
      }
    }
  }

  pickTool(tool) {
    this.setState({ tool });
    if (tool === 'drawing') {
      this.state.context?.setTool(tool, this.state.selectedColor);
    } else {
      this.state.context?.setTool(tool);
    }
  }

  changeColor = () => {
    const nextColorIndex = (colors.indexOf(this.state.selectedColor) + 1) % colors.length;
    this.setState({ selectedColor: colors[nextColorIndex], tool: 'drawing' });
    this.pickTool('drawing');
  };

  toggleColorPicker = () => {
    this.setState(prevState => ({ showColorPicker: !prevState.showColorPicker }));
  };

  // Apply the selected color to the Cobrowse annotation tool
  handleColorChange = (color) => {
    this.setState({ selectedColor: color });

    // Save the selected color in localStorage
    localStorage.setItem('cbio_selected_color', color);
    this.state.context?.setTool('drawing', color);
    this.pickTool('drawing');

    this.toggleColorPicker(); // Close the color picker after selecting a color
  };

  renderControls() {
    const { session, tool } = this.state;
    if (session?.state !== 'active') return null;
    return (
      <div className='agent-controls'>
        <div className='timer'>
          <Stopwatch start={session.activated} />
        </div>
        <div onClick={() => this.pickTool('laser')} title={getMessage("INBOUND_CO_BROWSE_LASER_POINTER")} className={`btn btn-left-most ${tool === 'laser' ? 'btn-selected' : ''}`}>
          <img src={ic_laser} alt={getMessage("INBOUND_CO_BROWSE_LASER_POINTER")}/>
        </div>
        <div onClick={this.toggleColorPicker} title={getMessage("INBOUND_CO_BROWSE_ANNOTATIONS_COLOR")} className={`btn btn-mid-left`}>
          <div 
            className="annotationsColor"
            style={{ backgroundColor: this.state.selectedColor }}
          />
        </div>
        {this.renderColorPicker()}
        <div onClick={() => this.pickTool('drawing')} title={getMessage("INBOUND_CO_BROWSE_DRAW")} className={`btn btn-mid ${tool === 'drawing' ? 'btn-selected' : ''}`}>
          <img src={ic_pencil} alt={getMessage("INBOUND_CO_BROWSE_DRAW")}/>
        </div>
        <div onClick={() => this.state.context.clearAnnotations()} title={getMessage("INBOUND_CO_BROWSE_CLEAR_DRAWING")} className='btn btn-mid-right'>
          <img src={ic_trash} alt={getMessage("INBOUND_CO_BROWSE_CLEAR_DRAWING")}/>
        </div>
        <div onClick={() => this.pickTool('control')} title={getMessage("INBOUND_CO_BROWSE_REMOTE_CONTROL")} className={`btn btn-solo ${tool === 'control' ? 'btn-selected' : ''}`}>
          <img src={ic_remote_control} alt={getMessage("INBOUND_CO_BROWSE_REMOTE_CONTROL")}/>
        </div>
        {/* <div onClick={() => this.state.context.setFullDevice(session.full_device === 'on' ? 'off' : 'requested')} title={'Full Device Mode'} className={`btn ${`full-device-${session.full_device}`}`}>
          <FontAwesomeIcon icon={faDesktop} />
        </div> */}
        <div onClick={() => this.state.context.endSession()} title={getMessage("INBOUND_CO_BROWSE_END")} className='btn btn-right-most btn-end'>
          <img src={ic_end_call} alt={getMessage("INBOUND_CO_BROWSE_END")}/>
        </div>
      </div>
    );
  }

  toggleInboundSideState() {
    if (this.props.isInboundCobrowseMinimize) {
      inboundCoBrowseAction.isInboundCoBrowseMinimize(false);
      inboundCoBrowseAction.showHideInboundCoBrowse(true);
      sessionActions.setComponentInFocus(COMPONENT_IN_FOCUS.INBOUND_COBROWSE);
    }
  }

  renderInboundMiniMask() {
    if (this.props.isInboundCobrowseMinimize) {
      return (
        <div className='mask' onClick={() => this.toggleInboundSideState()}></div>
      );
    }
    return null;
  }

  renderMaskForSearchingDevice() {
    if (this.state.maskSearchingForDevice) {
      return (
        <div className="maskSearchingForDevice">
          <div className="centeredContent">
            <div className="loader"></div>
            <div className="searchText">{getMessage("INBOUND_CO_BROWSE_SEARCHING_DEVICE")}</div>
            <div className="btn btn-end" onClick={this.handleEndCall}>
              <img src={ic_end_call} alt={getMessage("INBOUND_CO_BROWSE_END")} title={getMessage("INBOUND_CO_BROWSE_END")}/>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  handleEndCall = () => {
    this.setState({ maskSearchingForDevice: false });
    sessionActions.setComponentInFocus(this.props.showAudioVideoComponent ? COMPONENT_IN_FOCUS.AUDIO_VIDEO : COMPONENT_IN_FOCUS.CANVAS);
    inboundCoBrowseAction.isInboundCoBrowseMinimize(false);
    inboundCoBrowseAction.endInboundCoBrowse(true, this.props.sessionKey);
    inboundCoBrowseAction.showHideInboundCoBrowse(false);
  };
  
  render() {
    const isAllFeaturesUsed = this.props.componentInFocus === COMPONENT_IN_FOCUS.INBOUND_COBROWSE && this.props.showAudioVideoComponent && this.props.isScreenShare;
    return (
      <div className={`InboundCobrowseUi ${this.props.isInboundCobrowseMinimize ? 'InboundCobrowseUiPos2 minimized' :  
        isAllFeaturesUsed ? 'screenShareAudioInCobIsEnabled InboundCobrowseUiPos1' : 'InboundCobrowseUiPos1'}`}>
        <div className='iFrame'>
          {this.renderMaskForSearchingDevice()}
          {this.renderInboundMiniMask()}
          <iframe
            ref={this.onIframeRef}
            className='screen'
            title='inboundCobrowse'
            frameBorder={0}
            src={this.state.iFrameSrc}
          />
          {/* <div className='iFrame'>
            {this.renderControls()}
          </div> */}
        </div>
      </div>
    );
  }
}

export default InboundCobrowse;
